/**
 * Broker Link & Onboarding — phase 4 of broker integration.
 *
 * Lets a user link a new broker account to NigaStock. In production:
 *   1. User picks a broker on the Settings screen.
 *   2. NigaStock BAL POSTs to /v1/broker/{id}/link-begin which returns an
 *      OAuth 2.0 PKCE authorize URL for that broker's authorization server.
 *   3. Browser is redirected to the broker; user authenticates + consents.
 *   4. Broker redirects back to https://nigastock.ng/callback?code=...&state=...
 *   5. Callback handler POSTs to /v1/broker/{id}/link-complete which
 *      exchanges code for refresh_token and stores in Secrets Manager keyed
 *      on the user's Cognito sub. The access_token never touches the browser.
 *   6. From then on, BAL uses the per-user Secrets Manager entry when calling
 *      the broker; no credentials leak to the client.
 *
 * This module provides the client-side state machine and the per-broker link
 * status store (localStorage for the demo, server-backed in prod).
 */

import { BROKERS, type BrokerId, type BrokerSpec } from './brokers';

export type LinkStatus = 'linked' | 'unlinked' | 'pending' | 'error' | 'expired';

export interface BrokerLink {
  brokerId: BrokerId;
  status: LinkStatus;
  linkedAt?: string;
  linkedAccounts?: number;
  lastRefreshAt?: string;
  errorMessage?: string;
}

const KEY = 'nigastock.broker.links';

function safeLoad(): BrokerLink[] {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    if (!raw) return seedDefault();
    const arr = JSON.parse(raw) as BrokerLink[];
    if (!Array.isArray(arr)) return seedDefault();
    return arr;
  } catch { return seedDefault(); }
}
function safeSave(arr: BrokerLink[]) {
  try { localStorage.setItem(KEY, JSON.stringify(arr)); } catch {}
}
function seedDefault(): BrokerLink[] {
  // Demo starts with Bamboo linked (the default the trader already uses).
  return [
    { brokerId: 'bamboo', status: 'linked', linkedAt: new Date(Date.now() - 86400_000 * 14).toISOString(),
      linkedAccounts: 1, lastRefreshAt: new Date(Date.now() - 1800_000).toISOString() },
    ...BROKERS.filter((b) => b.id !== 'bamboo').map((b) => ({ brokerId: b.id, status: 'unlinked' as LinkStatus })),
  ];
}

export function listLinks(): BrokerLink[] {
  return safeLoad();
}
export function getLink(id: BrokerId): BrokerLink | undefined {
  return safeLoad().find((l) => l.brokerId === id);
}
export function setStatus(id: BrokerId, patch: Partial<BrokerLink>) {
  const arr = safeLoad();
  const i = arr.findIndex((l) => l.brokerId === id);
  if (i < 0) {
    arr.push({ brokerId: id, status: 'unlinked', ...patch });
  } else {
    arr[i] = { ...arr[i]!, ...patch } as BrokerLink;
  }
  safeSave(arr);
}

/**
 * Kick off the OAuth PKCE flow. In prod this returns the broker's authorize
 * URL and the caller redirects. In the demo we simulate the full round-trip
 * with a 2.5-second delay + automatic "code" exchange.
 */
export async function linkBroker(id: BrokerId, opts?: { onProgress?: (step: string) => void }): Promise<BrokerLink> {
  const broker = BROKERS.find((b) => b.id === id);
  if (!broker) throw new Error('unknown broker');
  setStatus(id, { status: 'pending' });
  opts?.onProgress?.('Generating PKCE challenge...');
  await delay(400);
  opts?.onProgress?.(`Redirecting to ${broker.label} authorization server...`);
  await delay(900);
  opts?.onProgress?.('Awaiting user consent...');
  await delay(700);
  opts?.onProgress?.('Exchanging authorization code for refresh_token...');
  await delay(500);
  opts?.onProgress?.('Writing credentials to Secrets Manager (user-scoped)...');
  await delay(300);
  const linked: BrokerLink = {
    brokerId: id,
    status: 'linked',
    linkedAt: new Date().toISOString(),
    linkedAccounts: 1,
    lastRefreshAt: new Date().toISOString(),
  };
  setStatus(id, linked);
  opts?.onProgress?.('Link complete.');
  return linked;
}

export async function unlinkBroker(id: BrokerId): Promise<void> {
  await delay(300);
  setStatus(id, { status: 'unlinked', linkedAt: undefined, linkedAccounts: 0, lastRefreshAt: undefined, errorMessage: undefined });
}

/** Refresh the OAuth access_token via the BAL (no-op in the demo). */
export async function refreshToken(id: BrokerId): Promise<BrokerLink> {
  await delay(400);
  setStatus(id, { status: 'linked', lastRefreshAt: new Date().toISOString() });
  return getLink(id)!;
}

export function labelOf(id: BrokerId): string {
  return BROKERS.find((b) => b.id === id)?.label ?? id;
}

function delay(ms: number) { return new Promise<void>((r) => setTimeout(r, ms)); }

/**
 * Aggregate view used by the Settings UI.
 */
export interface BrokerLinkRow {
  spec: BrokerSpec;
  link: BrokerLink;
}
export function rowsForSettings(): BrokerLinkRow[] {
  const links = listLinks();
  return BROKERS.map((spec) => ({
    spec,
    link: links.find((l) => l.brokerId === spec.id) ?? { brokerId: spec.id, status: 'unlinked' as LinkStatus },
  }));
}
